import { MAP_ELEMENT } from "../../../contexts/WorldMapContext/constants";
import { datetime } from "../../../util/library/datetime";
import { OpService } from "../../../util/services/operation";
import { selectFactory } from "../../../util/store/factory/constants";
import { selectOpMode } from "../../../util/store/selectors";
import { getStore } from "../../../util/store/store";
import {
    aDayMillisecond,
    AVAILABLE_TIME_TYPE,
    CERTAIN_TYPE,
    CONTRACT_TYPE,
    DELIVERY_TYPE,
    initAutoPlanConfig,
    OP_STATUS,
    OPERATION_CONFIRM_STATUS,
    OPERATION_TYPE,
    PriorityTime,
    showAlert,
    TASK_TYPE,
    TIME_PER_STEP,
    TREATMENT_TYPE,
    treatmentType,
    VESSEL_TYPE
} from "../../../util/varibles/constants";
import { cloneObj, getHourMinute, getStartFinishByOpMode, uuid } from "../../../util/varibles/global";
import {
    IActivityLog,
    IAfterProcesses,
    IDeliveryType,
    IFactory,
    IIdName,
    ILevel,
    ILevel2,
    ILevelByGroup,
    ILevelByOp,
    ILevelByVessel,
    ILevelByVessel2,
    IOperation,
    IOperation2,
    IOperations,
    ISorting,
    IStorage,
    ITreatment2,
    IUnit,
    IVessel,
    TOperation,
    TOperation2
} from "../../../util/varibles/interface";
import { notify, NotifyCode } from "../../../util/varibles/message";
import { IFishChanges } from "../constants";
import { HEIGHT_PER_DAY } from "../Plan/Calendar/Operations/constants";
import { getTimeByOpMode } from "./function_operation/constants";
import { IResultValidation, opValidation, planValidation, valid, VALIDATION, VALIDATION_STATUS } from "./validation";

/*
    Date: 27/03/2020
    Author: Vinh.Pham
    Description: Check valid operation
 */
export interface ICheckValidOperation {
    operations: IOperations,
    level: ILevelByVessel
    data: IOperation,
    factories?: IFactory[]
    allowPast?: boolean
}

export function checkValidOperation(args: ICheckValidOperation): IResultValidation {
    const {
        operations,
        level,
        data,
        factories = [],
        allowPast = false,
    } = args;
    const actions: VALIDATION[] = cloneObj(opValidation[data.operation.operation_type])
        .filter((action: VALIDATION) => !allowPast || action !== VALIDATION.PAST);
    const opMode = selectOpMode(getStore());

    return actions.reduce((rs: IResultValidation, key, i, arr) => {
        const common = { operations, level, factories, opMode };
        const result = planValidation[key](data, common);
        if (result.status === VALIDATION_STATUS.ERROR) {
            arr.splice(i);
            return result;
        }
        if (result.status === VALIDATION_STATUS.WARN)
            return result;
        return rs;
    }, valid);
}

/*
    Date: 10/01/2020
    Author: Vinh.Pham
    Description: check factory time
 */
export function compareFactoryTime(factoryTime: number, workTime: number) {
    const hoursFactory = new Date(factoryTime).getHours(),
        minutesFactory = new Date(factoryTime).getMinutes(),
        newTime = hoursFactory + minutesFactory / 60;
    if (newTime > workTime)
        return -1;
    else if (newTime === workTime)
        return 0;
    else
        return 1;
}

/*
    Date: 12/08/2020
    Author: Vinh.Pham
*   Description: calculate total fish and total weight
 */
export function getTotalAmountAndTotalWeight(data: any): { total_amount: number, total_weight: number } {
    return data.reduce((total: { total_amount: number, total_weight: number }, unit: IUnit) => {
        total.total_amount += unit.fish_amount;
        total.total_weight += unit.total_weight;
        return total;
    }, { total_amount: 0, total_weight: 0 });
}

export function getAmountAndWeightFromGroup(data: { fish_amount: number, total_weight: number }[]): {
    fish_amount: number,
    total_weight: number
} {
    return data.reduce((data: { fish_amount: number, total_weight: number }, { fish_amount, total_weight }) => {
        data.fish_amount += fish_amount;
        data.total_weight += total_weight;
        return data;
    }, { fish_amount: 0, total_weight: 0 });
}

export function getAmountAndWeightFromAllDeliveryTypes(data: IUnit[]) {
    let sumFishAmount = 0, sumTotalWeight = 0;
    const sub_operations = data.map((unit) => {
        const { fish_amount, total_weight } = getAmountAndWeightFromGroup(unit.delivery_types || []);
        sumFishAmount += fish_amount;
        sumTotalWeight += total_weight;
        return { ...unit, fish_amount, total_weight };
    });
    return { sub_operations, total_amount: sumFishAmount, total_weight: sumTotalWeight };
}


const commonParamActivityLog = (args: any) => {
    const { data } = args;
    let result: any = {
        vessel_id: data.vessel.id,
        vessel_tenant_id: data.tenant_id,
        weather: false,
        activity_log: true,
    };

    if (data.operation.status === OP_STATUS.PROCESSING)
        result.operation_id = data.operation.id;

    return result;
};

export interface IProperty {
    start: number
    start_place?: {
        start_place_id: string
        start_place_field: string
        start_time?: number
    },
    opIds?: string[]
    support_vessel?: boolean
    after_round?: Record<string, IAfterProcesses[]>
}

interface IParamActivityLog {
    setting?: {
        autoPlanConfig: any
        sitesTime: any
        factoriesTime: any
    };
    property: IProperty;
}

export const paramsActivityLog: {
    [id: string]: (data: { operation: TOperation, vessel: IVessel }, args: IParamActivityLog) => any
} = {
    [OPERATION_TYPE.HARVEST]: (data, args) => {
        const startTime = args.property.start;
        const {
            start_place_id,
            start_place_field,
            start_time,             // Limit start
        } = args.property.start_place || getLastPlaceByTime(data.vessel.id, startTime, args.property.opIds);
        const { site_id, factory_id } = data.operation;
        const {
            factory_time,
            site_arrival_time,
            factoryTime,
            priority
        } = getFactorySiteTime(site_id, factory_id, data.vessel.type, start_time || Date.now());

        const { sub_operations } = data.operation;
        const { total_amount, total_weight } = getTotalAmountAndTotalWeight(sub_operations);
        return {
            data: {
                ...data,
                operation: { ...data.operation, start_place_id, start_place_field, total_amount, total_weight }
            },
            param: {
                ...commonParamActivityLog({ data }),
                operation_type: OPERATION_TYPE.HARVEST,
                sites: data.operation.sites,
                start_time,
                start_place_id,
                start_place_field,
                is_journey_to_site: data.operation.is_journey_to_site,
                is_cleaning: data.operation.is_cleaning,
                source_id: site_id,
                destination_id: factory_id,
                router_id: data.operation.router_id,
                factory_time,
                packing_time: factoryTime,
                site_arrival_time,
                priority,
                delivery_types: sub_operations.reduce((list: any, { delivery_types = [] }: any) => [
                    ...list,
                    ...delivery_types.map(({ id, total_weight }: any) => ({ id, total_weight }))
                ], []),
                total_amount,
                total_weight,
            }
        };
    },
    [OPERATION_TYPE.TREATMENT]: (data, args) => {
        const { site_id, is_cleaning, unit_order, sub_operations, tasks, round_map } = data.operation;
        const startTime = args.property.start;
        const { support_vessel, after_round } = args.property;
        return {
            data,
            param: {
                ...commonParamActivityLog({ data }),
                sub_operations,
                operation_type: OPERATION_TYPE.TREATMENT,
                est_start_time: startTime,
                is_cleaning,
                support_vessel,
                after_round,
                source_id: site_id,
                unit_order,
                round_map,
                tasks
            }
        };
    },
    [OPERATION_TYPE.TRANSPORT]: (data, args) => {
        const { router_id, sub_operations, source_id, destination_id } = data.operation;
        const startTime = args.property.start;

        const {
            start_place_id,
            start_place_field,
            start_time,             // Limit start
        } = args.property.start_place || getLastPlaceByTime(data.vessel.id, startTime, args.property.opIds);

        const { total_amount, total_weight } = getTotalAmountAndTotalWeight(sub_operations);
        const { sitesTime } = args.setting || {};

        const siteTime = getTimeSetting(sitesTime, source_id, -1);
        let site_arrival_time = -1;
        if (siteTime !== -1) {
            site_arrival_time = datetime(startTime).set(getHourMinute(siteTime)).time;
        }

        return {
            data: {
                ...data,
                operation: { ...data.operation, start_place_id, start_place_field, total_amount, total_weight }
            },
            param: {
                ...commonParamActivityLog({ data }),
                operation_type: OPERATION_TYPE.TRANSPORT,
                start_time,
                start_place_id,
                start_place_field,
                router_id,
                source_id,
                destination_id,
                site_arrival_time,
                est_start_time: startTime,
                total_amount,
                total_weight,
            }
        };
    },
    [OPERATION_TYPE.SERVICE]: (data, args) => {
        const { operation: { tasks, crew_tasks } } = data;
        const startTime = args.property.start;
        return {
            data,
            param: {
                ...commonParamActivityLog({ data }),
                operation_type: OPERATION_TYPE.SERVICE,
                est_start_time: startTime,
                tasks,
                crew_tasks
            }
        };
    }
};

export const getActivityLogDurationEst = async (
    data: Pick<IOperation, 'operation' | 'vessel'> & Partial<Pick<IOperation, 'activity_log'>>,
    abort?: AbortController,
    includeService = false
) => {
    const { operation_type, duration_est, id } = data.operation;
    if (duration_est !== undefined || (operation_type !== OPERATION_TYPE.HARVEST && operation_type !== OPERATION_TYPE.TREATMENT
        && !(includeService && operation_type === OPERATION_TYPE.SERVICE)))
        return duration_est;

    // Use the saved operation before the pending edit or drag to establish the baseline.
    const current = getStore().planOperation.operations[id] || data;
    if (!current.activity_log || current.activity_log.length < 2)
        return undefined;

    const start = current.activity_log[0].est_start_time;
    const { param } = paramsActivityLog[operation_type](current, {
        property: {
            start,
            opIds: [id],
            start_place: {
                start_place_id: current.operation.start_place_id || '-',
                start_place_field: current.operation.start_place_field || '-',
                start_time: start
            }
        }
    });
    const { activity_log } = await OpService.activityLog(param, abort);
    return activity_log[activity_log.length - 1].est_start_time - activity_log[0].est_start_time;
};

export function getTimeSetting(dataTime: any, key: string, init: number): number {
    return dataTime.all ? dataTime.all.arrival_time : (dataTime[key] ? dataTime[key].arrival_time : init);
}

export const sourceKeys: any = {
    [OPERATION_TYPE.HARVEST]: {
        key: "site_id",
        type: "site",
        nameText: "Site",
        name: "site_name",
        map_id: MAP_ELEMENT.SITE
    },
    [OPERATION_TYPE.TRANSPORT]: {
        key: "source_id",
        type: "site",
        nameText: "Site",
        name: "site_name",
        map_id: MAP_ELEMENT.SITE
    },
    [OPERATION_TYPE.TREATMENT]: {
        key: "site_id",
        type: "site",
        nameText: "Site",
        name: "site_name",
        map_id: MAP_ELEMENT.SITE
    },
    [OPERATION_TYPE.SERVICE]: {
        key: "site_id",
        type: "site",
        nameText: "Site",
        name: "site_name",
        map_id: MAP_ELEMENT.SITE
    },
};

export const destinationKeys: any = {
    [OPERATION_TYPE.HARVEST]: {
        key: "factory_id",
        type: "factory",
        nameText: "Factory",
        name: "factory_name",
        map_id: MAP_ELEMENT.FACTORY
    },
    [OPERATION_TYPE.TRANSPORT]: {
        key: "destination_id",
        type: "site",
        nameText: "Smolt site",
        name: "destination_name",
        map_id: MAP_ELEMENT.SITE
    },
    [OPERATION_TYPE.TREATMENT]: {
        key: "site_id",
        type: "site",
        nameText: "Site",
        name: "site_name",
        map_id: MAP_ELEMENT.SITE
    },
    [OPERATION_TYPE.SERVICE]: {
        key: "site_id",
        type: "site",
        nameText: "Site",
        name: "site_name",
        map_id: MAP_ELEMENT.SITE
    },
};

export const getParamsWeatherOfOp = (op: TOperation2, extraProperty: any) => {
    const { id } = op;
    const common = {
        ...extraProperty,
        operation_id: id
    }
    switch (op.operation_type) {
        case OPERATION_TYPE.HARVEST:
            return { ...common, source_id: op.site_id, destination_id: op.factory_id };
        case OPERATION_TYPE.TRANSPORT:
            return { ...common, source_id: op.source_id, destination_id: op.destination_id };
        case OPERATION_TYPE.TREATMENT:
            return { ...common, source_id: op.site_id, destination_id: op.site_id };
        case OPERATION_TYPE.SERVICE:
            return { ...common, source_id: op.site_id, destination_id: op.site_id };
        case OPERATION_TYPE.EVENT:
            return { ...common, pois: op.pois ?? [], };
        case OPERATION_TYPE.SUPPORT:
            return { ...common, locations: op.locations, };
        default:
            return common;
    }
};

interface IResultGetLastPlaceByTime {
    start_place_id: string;
    start_place_field: string;
    start_place_name: string;
    start_time: number;
}

export function getLastPlaceByTime(vessel_id: string, start: number, opIds: string[] = []): IResultGetLastPlaceByTime {
    const state = getStore();
    const { operations, level } = state.planOperation;
    const opMode = selectOpMode(state);
    const vesselLevel = level[vessel_id] || {};
    let start_place_id = "", start_place_field = "", start_place_name = "", start_time = 0;
    const ids = new Set(opIds);
    Object.keys(vesselLevel).reduce((time: any, key) => {
        const { activity_log, operation, parallel }: any = operations[key];
        if (ids.has(operation.id || ""))
            return time;
        const finishTime = getTimeByOpMode[opMode](activity_log[activity_log.length - 1]);
        if (finishTime >= start)
            return time;

        if (start_time < finishTime && !parallel) {
            start_time = finishTime;
        }

        if (operation.operation_type !== OPERATION_TYPE.EVENT
            && (!time || time < finishTime)
            && destinationKeys[operation.operation_type]) {
            const { key, type, name } = destinationKeys[operation.operation_type];
            start_place_id = operation[key];
            start_place_field = type;
            start_place_name = operation[name] || "";
            return finishTime;
        }
        return time;
    }, undefined);

    return {
        start_place_id,
        start_place_field,
        start_place_name,
        start_time: start_time ? Math.ceil((start_time + TIME_PER_STEP) / TIME_PER_STEP) * TIME_PER_STEP : 0
    };
}


/*
    Date: 03/01/2025
    Author: Vinh.Pham
    Description: Get factory_time and site_arrival_time and priority from config of user (Only work at Plan page)
 */
export function getFactorySiteTime(site_id: string, factory_id: string, vesselType: VESSEL_TYPE, start: number): {
    factory_time: number
    factoryTime: number
    priority: PriorityTime
    site_arrival_time: number
} {
    const factories = selectFactory.data(getStore());
    const { work_time = 360 } = factories.find(item => item.id === factory_id) || {};
    const { setting } = getStore().login.user;
    const { priority } = (setting.autoPlanConfig || initAutoPlanConfig).vessel[vesselType] || {};
    const factoryTime = getTimeSetting(setting?.factoriesTime || {}, factory_id, work_time);
    const siteTime = getTimeSetting(setting?.sitesTime || {}, site_id, -1);
    const factory_time = datetime(start).set(getHourMinute(factoryTime)).time;
    let site_arrival_time = -1;
    if (siteTime !== -1) {
        site_arrival_time = datetime(factory_time).set(getHourMinute(siteTime)).time;
        if (site_arrival_time > factory_time)
            site_arrival_time -= aDayMillisecond;
    }

    return {
        factory_time,
        factoryTime,
        site_arrival_time,
        priority
    };
}

/*
    Date: 29/11/2020
    Author: Vinh.Pham
    Description: Check exist route
 */
export function checkRoute(source: any, destination: any, routeIds: string[]) {
    const route = new Set(routeIds).has([source.id, destination.id].join("_"));
    return route ? { error: VALIDATION_STATUS.VALID, message: "" } : {
        error: VALIDATION_STATUS.ERROR, message: notify[NotifyCode.E20]([source.name, destination.name])
    };
}

export function getFactory(factory_id: string, primary_factory: string, site: IIdName, factories: IFactory[]): {
    factory: IFactory,
    error_detail?: { [id: string]: any }
} {
    let id: string | undefined, factory;
    if (factory_id.length > 0) {
        id = factory_id;
    } else if ((primary_factory || "").length > 0) {
        id = primary_factory;
    }
    if (id)
        factory = factories.find(item => item.id === id);

    const { routeIds } = getStore().planOperation;

    if (factory) {
        const { error, message } = checkRoute(site, factory, routeIds);

        return {
            factory,
            error_detail: error === VALIDATION_STATUS.ERROR ? {
                route: [{ source_id: site.id, destination_id: factory.id, message }]
            } : {}
        }
    }

    factory = factories.find(item => checkRoute(site, item, routeIds).error === VALIDATION_STATUS.VALID);
    if (factory) {
        return { factory, error_detail: {} }
    } else {
        return {
            factory: factories[0],
            error_detail: {
                route: [{
                    source_id: site.id,
                    destination_id: factories[0]?.id,
                    message: notify[NotifyCode.E20]([site.name, factories[0]?.name || ''])
                }]
            }
        }
    }
}

export function getDeliveryType(factory: IFactory, vessel: IVessel): {
    deliveryType?: IDeliveryType,
    error_detail?: { [id: string]: any }
} {
    const { deliver_type = {} } = factory || {};
    const { type } = vessel;
    if (type === VESSEL_TYPE.PROCESS_VESSEL) {
        const valid = Object.keys(deliver_type).filter(key => deliver_type[key].type === DELIVERY_TYPE.DIRECT);
        if (valid.length > 0) {
            const keyDeliveryType = valid.find(key => deliver_type[key].default) || valid[0];
            return {
                deliveryType: deliver_type[keyDeliveryType],
            }
        } else {
            return {
                error_detail: {
                    deliveryType: notify[NotifyCode.E42]([factory.name])
                }
            }
        }
    } else {
        const keyDeliveryType = Object.keys(deliver_type).find(key => deliver_type[key].default) || Object.keys(deliver_type)[0];
        return {
            deliveryType: deliver_type[keyDeliveryType],
        }
    }
}


interface IUpdateNextOperation {
    startTime: number;
    finishTime: number;
    tenantId: string;
    ids: string[];
    vesselId: string;
    operations: IOperations;
    level: ILevelByVessel;
}

export const updateNextOperation = (args: IUpdateNextOperation): IOperations => {
    let { startTime, finishTime, tenantId, level, operations, vesselId } = args;
    const ids = new Set(args.ids || []);

    let { opsAllowPush, opsBlock } = Object.keys(level[vesselId] || {}).reduce((rs: { opsAllowPush: any; opsBlock: IOperation2[] }, key) => {
        const item = operations[key];
        const currentStart = item.activity_log[0].est_start_time;
        const { tenant_id: currentTenantId, id: currentOpId, status, parallel } = item.operation;
        if (currentStart < startTime || ids.has(currentOpId) || parallel)
            return rs;
        if (!statusUnAllow.has(status) && tenantId === currentTenantId) {
            rs.opsAllowPush.push(item as IOperation2);
        } else
            rs.opsBlock.push(item as IOperation2);
        return rs;
    }, {
        opsAllowPush: [],
        opsBlock: []
    });

    opsAllowPush.sort((a: any, b: any) => a.activity_log[0].est_start_time - b.activity_log[0].est_start_time);
    opsBlock.sort((a, b) => a.activity_log[0].est_start_time - b.activity_log[0].est_start_time);
    const operationsNew: any = {};
    let isWarn = false;
    const pusher = args.ids.reduce((rs: Record<string, boolean>, item) => {
        rs[item] = true;
        return rs;
    }, {});
    for (let i = 0; i < opsAllowPush.length; i++) {
        const { id } = opsAllowPush[i].operation;
        const currentStart = opsAllowPush[i].activity_log[0].est_start_time;
        if (currentStart <= finishTime) {
            console.log(opsAllowPush[i].operation.operation_code);
            const { isJump, ...args } = execPushOp({
                finishTime,
                currentStart,
                data: opsAllowPush[i],
                operations: opsBlock,
            });

            if (isJump)
                isWarn = true;

            operationsNew[id] = {
                ...opsAllowPush[i],
                ...args,
                action_type: opsAllowPush[i].action_type || OPERATION_CONFIRM_STATUS.UPDATE,
                pusher: { ...opsAllowPush[i].pusher || {}, ...pusher }
            };
            pusher[id] = true;
            finishTime = args.activity_log[args.activity_log.length - 1].est_start_time;
        } else
            break;
    }

    if (isWarn)
        showAlert.pushOp();

    return operationsNew;
};

const generateCommon = (currentOp: IOperation2, expandValues: Partial<TOperation2> = {}) => {
    const { vessel } = currentOp;
    const is_send_to_vessel = !!(vessel.isOwn || vessel.permission);
    const data: any = {
        checklist_ids: currentOp.operation.checklist_ids || [],
        operation_code: `${currentOp.operation.operation_code}`,
        state: currentOp.action_type || OPERATION_CONFIRM_STATUS.UPDATE,
        note: currentOp.operation.note || "",
        operation_type: currentOp.operation.operation_type,
        activity_log: currentOp.activity_log,
        is_send_to_vessel,
        status: is_send_to_vessel ? OP_STATUS.WAITING : OP_STATUS.PENDING,
        available_time_id: currentOp.operation.available_time_id || "-",
        certain_mode: currentOp.operation.certain_mode || CERTAIN_TYPE.NONE,
        related_id: currentOp.operation.related_id,
        tenant_id: currentOp.operation.tenant_id,
        invoices: currentOp.operation.invoices,
        // start_point: currentOp.operation.start_point,
        ...expandValues,
    };

    const tenantId = getStore().login.user.tenant_id;

    if (!vessel.isOwn) {
        if (currentOp.operation.contract_id && currentOp.operation.contract_id !== "-")
            data.contract_id = currentOp.operation.contract_id;
        else if (vessel.permission) {
            data.contract_id = vessel.permission;
        } else {
            const { id: contractId } = vessel.contracts.find(sub => sub.tenant_id === tenantId && sub.type === CONTRACT_TYPE.SINGLE) || { id: "-" };
            data.contract_id = contractId;
        }
    }

    if (currentOp.action_type !== OPERATION_CONFIRM_STATUS.NEW) {
        data.operation_id = `${currentOp.operation.id}`;
    }

    return data;
};

// Generate operation to confirm
export const generate = (
    data: IOperation2,
    expandValues?: Partial<TOperation2>
) => {
    const { operation } = data;
    switch (operation.operation_type) {
        case OPERATION_TYPE.HARVEST: {
            const rs: any = {
                sites: operation.sites || [],
                sub_operations: operation.sub_operations,
                site_id: operation.site_id,
                site_name: operation.site_name,
                tanks: operation.tanks,
                source_site_type: operation.source_site_type,
                factory_id: operation.factory_id,
                factory_name: operation.factory_name,
                router_id: operation.router_id || "",
                prev_op_router_id: operation.prev_op_router_id || "",
                is_journey_to_site: operation.is_journey_to_site,
                is_cleaning: operation.is_cleaning,
                start_place_id: operation.start_place_id || "-",
                start_place_field: operation.start_place_field || "-",
                destination_id: operation.factory_id,
                duration_est: operation.duration_est,
            };

            if (operation.sorting) {
                rs.sorting = operation.sorting;
            }

            return {
                ...rs,
                ...generateCommon(data),
            };
        }

        case OPERATION_TYPE.TREATMENT:
            return {
                start_place_id: "-",
                start_place_field: "-",
                sub_operations: operation.sub_operations,
                site_id: operation.site_id,
                site_name: operation.site_name,
                tasks: operation.tasks,
                files: operation.files,
                invoices: operation.invoices || [],
                is_cleaning: operation.is_cleaning,
                round_map: operation.round_map,
                duration_est: operation.duration_est,
                ...generateCommon(data),
            };

        case OPERATION_TYPE.TRANSPORT:
            return {
                sub_operations: operation.sub_operations,
                sub_destinations: operation.sub_destinations,
                source_id: operation.source_id,
                source_name: operation.source_name,
                source_site_type: operation.source_site_type,
                destination_id: operation.destination_id,
                destination_name: operation.destination_name,
                router_id: operation.router_id || "",
                prev_op_router_id: operation.prev_op_router_id || "",
                start_place_id: operation.start_place_id || "-",
                start_place_field: operation.start_place_field || "-",
                ...generateCommon(data),
            };

        case OPERATION_TYPE.EVENT:
            return {
                parallel: operation.parallel,
                event_type: operation.event_type,
                pois: operation.pois,
                ...generateCommon(data),
            };

        case OPERATION_TYPE.SERVICE:
            return {
                parallel: operation.parallel,
                request_tab_id: operation.request_tab_id,
                sub_operations: operation.sub_operations,
                start_place_id: "-",
                start_place_field: "-",
                site_id: operation.site_id,
                site_name: operation.site_name,
                tasks: operation.tasks,
                crew_tasks: operation.crew_tasks,
                ...(operation.duration_est === undefined ? {} : { duration_est: operation.duration_est }),
                ...generateCommon(data, expandValues),
            };

        case OPERATION_TYPE.SUPPORT:
            return {
                locations: operation.locations,
                ...generateCommon(data),
            };
        default:
            return;
    }
};


export function convertPxToTime(value: number): number {
    return Math.floor(value / HEIGHT_PER_DAY * aDayMillisecond);
}

export function convertPxToTimeByStep(value: number) {
    return Math.round(value / HEIGHT_PER_DAY * aDayMillisecond / TIME_PER_STEP) * TIME_PER_STEP;
}

/*
    Date: 13/01/2022
    Author: Vinh.Pham
    Description: Check after change time of operation A, if operation A placed above another operation B,
                 operation B will more 15 minute until operation A is not overlap on operation B
 */
export const statusUnAllow: any = new Set([
    OP_STATUS.FINISHED,
    OP_STATUS.PROCESSING,
]);

export function pushOperations(data: IOperation, operations: IOperations, isAll: boolean = true) {
    const { activity_log } = data;
    const startTime = activity_log[0].est_start_time;
    const { id: vesselId } = data.vessel;
    const { id: opId, tenant_id: tenantId } = data.operation;
    let { list, operationsNext } = Object.keys(operations).reduce((rs: any, key) => {
        const item = operations[key];
        const currentStart = item.activity_log[0].est_start_time;
        const { id: currentVesselId } = item.vessel;
        const { tenant_id: currentTenantId, id: currentOpId, status, parallel } = item.operation;
        if (vesselId !== currentVesselId || currentStart < startTime || currentOpId === opId || parallel)
            return rs;

        if (!statusUnAllow.has(status) && tenantId === currentTenantId) {
            rs.list.push(item);
        } else
            rs.operationsNext.push(item);
        return rs;
    }, {
        list: [],
        operationsNext: []
    });

    list.sort((a: any, b: any) => a.activity_log[0].est_start_time - b.activity_log[0].est_start_time);
    operationsNext.sort((a: any, b: any) => a.activity_log[0].est_start_time - b.activity_log[0].est_start_time);
    let finishTime = activity_log[activity_log.length - 1].est_start_time;
    let operationsNew: any = {}, isWarn = false;
    const pusher = { [opId]: true };

    for (let i = 0; i < list.length; i++) {
        const { id } = list[i].operation;
        const currentStart = list[i].activity_log[0].est_start_time;
        if (currentStart <= finishTime) {
            const { isJump, ...args } = execPushOp({
                finishTime,
                currentStart,
                data: list[i],
                operations: operationsNext,
            });

            if (isJump)
                isWarn = true;

            operationsNew = {
                ...operationsNew,
                [id]: {
                    ...list[i],
                    action_type: list[i].action_type || OPERATION_CONFIRM_STATUS.UPDATE,
                    ...args,
                    pusher: { ...list[i].pusher || {}, ...pusher }
                }
            };
            pusher[id] = true;
            finishTime = args.activity_log[args.activity_log.length - 1].est_start_time;
        } else
            break;
    }
    if (isWarn)
        showAlert.pushOp();

    if (isAll)
        return { ...operations, ...operationsNew };
    else
        return { ...operationsNew, [opId]: data };
}

const updateActivityLog: {
    [id: string]: (data: IOperation2, duration: number) => { activity_log: IActivityLog[], operation?: TOperation2 },
    default: (data: IOperation2, duration: number) => { activity_log: IActivityLog[], operation?: TOperation2 }
} = {
    [OPERATION_TYPE.HARVEST]: (data, duration) => {
        return {
            activity_log: data.activity_log.map((item) => {
                const { est_finish_time, sub_steps } = item;
                const value = {
                    ...item,
                    est_start_time: item.est_start_time + duration,
                }
                if (est_finish_time) {
                    value.est_finish_time = est_finish_time + duration;
                }
                if (sub_steps) {
                    value.sub_steps = sub_steps.map(sub => ({
                        ...sub,
                        est_start_time: sub.est_start_time + duration,
                        est_finish_time: sub.est_finish_time + duration
                    }))
                }

                return value
            })
        }
    },
    [OPERATION_TYPE.TREATMENT]: (data, duration) => {
        const { round_map = {} } = data.operation as ITreatment2;
        return {
            activity_log: data.activity_log.map((item) => ({
                ...item,
                est_start_time: item.est_start_time + duration
            })),
            operation: {
                ...data.operation,
                round_map: Object.keys(round_map).reduce((rs: typeof round_map = {}, key) => {
                    const { tanks = [], after_processes = [] } = round_map[key];
                    rs[key] = {
                        tanks: tanks.map(tank => {
                            const { processes = [] } = tank;
                            return {
                                ...tank,
                                processes: processes.map(sub => {
                                    const { start_time, finish_time } = sub;
                                    return {
                                        ...sub,
                                        start_time: start_time + duration,
                                        finish_time: finish_time + duration
                                    }
                                })
                            }
                        }),
                        after_processes: after_processes.map(sub => {
                            const { start_time, finish_time } = sub;
                            if (start_time)
                                sub.start_time = start_time + duration;
                            if (finish_time)
                                sub.finish_time = finish_time + duration;
                            return sub;
                        })
                    }
                    return rs;
                }, {})
            }
        }
    },
    default: (data, duration) => ({
        activity_log: data.activity_log.map(item => ({
            ...item,
            est_start_time: item.est_start_time + duration
        }))
    })
}

interface IExecPushOp {
    finishTime: number;
    currentStart: number;
    data: IOperation2;
    operations: IOperation2[];
}

export function execPushOp(args: IExecPushOp) {
    const { finishTime, currentStart, data, operations } = args;
    const duration = calcDurationToPush(finishTime, currentStart);

    let start = data.activity_log[0].est_start_time + duration,
        finish = data.activity_log[data.activity_log.length - 1].est_start_time + duration,
        isJump = false;
    for (let i = 0; i < operations.length; i++) {
        const { activity_log: targetActivityLog } = operations[i];
        const startFinish = targetActivityLog[0].est_start_time;
        const targetFinish = targetActivityLog[targetActivityLog.length - 1].est_start_time;
        if (start <= targetFinish && startFinish <= finish) {
            const duration = calcDurationToPush(targetFinish, start);
            isJump = true;
            start += duration;
            finish += duration;
        }
    }
    const newDuration = start - currentStart;
    const { [data.operation.operation_type]: update = updateActivityLog.default } = updateActivityLog;
    return {
        ...update(data, newDuration),
        isJump
    };
}

function calcDurationToPush(finishOther: number, startTarget: number) {
    return (Math.ceil((finishOther - startTarget) / TIME_PER_STEP) + 2) * TIME_PER_STEP;
}

export function convertOpsFromResponse(operations: IOperation[], vessels: IVessel[], old: { operations: IOperations, level: ILevelByVessel } = { operations: {}, level: {} }): { operations: IOperations, level: ILevelByVessel, vessels: IVessel[] } {
    return operations.reduce((rs: { operations: IOperations, level: ILevelByVessel, vessels: IVessel[] }, item) => {
        if (!item.operation) {
            return rs;
        }

        const { vessel_id = "", id } = item.operation;
        const vessel = vessels.find(vessel => vessel.id === vessel_id);
        if (vessel) {
            const value = { ...item, vessel };
            rs.operations[id] = value
            rs.level = upLevel(rs.level, value, rs.operations);
            const { row = 1 } = rs.vessels[vessel.index] || {};
            vessels[vessel.index].row = Math.max(row, rs.level[vessel_id][id].sub);
        }
        return rs;
    }, { ...old, vessels })
}

export function upLevel2(source: ILevelByVessel2, data: IOperation, ops: IOperations) {
    if (!data)
        return source;

    const { id, operation_code, status, parallel = false } = data.operation;
    const { id: vessel_id } = data.vessel;
    const opMode = selectOpMode(getStore());
    const levelVessel: ILevelByGroup = cloneObj(source[vessel_id] || {});
    const { start: startA, finish: finishA } = getStartFinishByOpMode[opMode](data.activity_log, status);
    const isOverLap = (op: IOperation) => {
        const { start: startB, finish: finishB } = getStartFinishByOpMode[opMode](op.activity_log, op.operation.status);
        return id !== op.operation.id && startA <= finishB && startB <= finishA
    }
    if (Object.keys(levelVessel).length > 0) {
        const newGroups: typeof levelVessel = {};
        let index = 0,
            count = 1,
            limit = new Set<number>(parallel ? [] : [0]),
            newGroupId: string | undefined;


        const updateState: (other: ILevel2) => ILevel2 = parallel ? other => {
            const { index: otherIndex } = other;
            limit.add(otherIndex);
            count++;
            if (index === otherIndex) {
                const maxCount = Math.max(other.count, count);
                for (let i = index; i <= maxCount; i++) {
                    if (!limit.has(i)) {
                        index = i;
                        break;
                    }
                }
            }
            if (other.count === index) {
                other.count++;
            }
            return other;
        } : other => {
            count++;
            // other.count++;
            const { index: otherIndex } = other;
            if (limit.has(otherIndex)) {
                const maxCount = Math.max(other.count, count);
                for (let i = index; i < maxCount; i++) {
                    if (!limit.has(i)) {
                        other.index = i;
                        limit.add(i);
                        break;
                    }
                }
            }
            return other
        }
        Object.keys(levelVessel).forEach(groupId => {
            const group = cloneObj(levelVessel[groupId]);
            const newGroup: typeof group = {};
            let isGroupOverlap = false;
            Object.keys(group).forEach(opId => {
                const item = ops[opId];
                if (isOverLap(item)) {
                    isGroupOverlap = true;
                    newGroup[opId] = updateState(group[opId])
                } else {
                    newGroup[opId] = group[opId];
                }
            })
            if (isGroupOverlap) {
                if (newGroupId) {
                    newGroups[newGroupId] = { ...newGroups[newGroupId], ...newGroup }
                } else {
                    newGroupId = groupId
                    newGroups[groupId] = newGroup;
                }
                limit = new Set([index]);
            } else {
                newGroups[groupId] = newGroup;
            }
        })
        if (newGroupId) {
            newGroups[newGroupId] = { ...newGroups[newGroupId] || {}, [id]: { operation_code, index, count } }
            return { ...source, [vessel_id]: newGroups };
        }
    }
    const groupId = uuid();
    levelVessel[groupId] = { ...levelVessel[groupId] || {}, [id]: { operation_code, index: 0, count: 1 } }
    return { ...source, [vessel_id]: levelVessel };
}

export function upLevel(source: ILevelByVessel, data: IOperation, ops: IOperations, levelOld?: ILevelByOp) {
    if (!data)
        return source;

    const { id, operation_code, status, parallel = false } = data.operation;
    const { id: vessel_id } = data.vessel;
    const opMode = selectOpMode(getStore());

    const levelVessel: ILevelByOp = cloneObj(source[vessel_id] || {});
    const { start: startA, finish: finishA } = getStartFinishByOpMode[opMode](data.activity_log, status);
    const value: ILevel = { main: true, operation_code, level: 1, sub: 0, ids: [] };

    const ids = new Set<string>([]);
    const yes: { false: { [id: string]: boolean }, true: { [id: string]: boolean } } = { false: {}, true: {} };

    Object.keys(levelVessel).forEach(key => {
        const item = ops[key];
        if (!item)
            return;
        const { parallel: otherParallel, status: otherStatus } = item.operation;
        if (parallel || (!parallel && otherParallel)) {
            const { start: startB, finish: finishB } = getStartFinishByOpMode[opMode](item.activity_log, otherStatus);
            if (id !== key && startA <= finishB && startB <= finishA) {
                const { main } = levelVessel[key];
                if (levelVessel[key].ids.length === 0) {
                    const sub = levelVessel[key].sub + 1;
                    levelVessel[key] = { ...levelVessel[key], sub, };
                    value.sub = sub;
                    value.level = sub;
                } else if (levelVessel[key].sub === value.sub) {
                    if (levelVessel[key].level === value.level) {
                        if (levelVessel[key].level >= levelVessel[key].sub) {
                            const isExist = Array.from(ids).some(subKey => new Set(levelVessel[subKey].ids).has(key));
                            if (isExist) {
                                const sub = levelVessel[key].sub + 1;
                                levelVessel[key] = {
                                    ...levelVessel[key],
                                    sub,
                                    level: Math.min(levelVessel[key].level + 1, sub)
                                };
                                value.sub = levelVessel[key].sub;
                                value.level = value.sub;
                            }
                        } else {
                            value.level++;
                        }
                    } else if (value.main)
                        value.level = Math.max(value.level, levelVessel[key].level);
                } else {
                    const sub = Math.max(levelVessel[key].sub, value.sub);
                    levelVessel[key] = { ...levelVessel[key], sub };
                    value.sub = sub;
                }

                if (main) {
                    if (otherParallel) {
                        if (ids.size >= levelVessel[key].ids.length || !parallel) {
                            levelVessel[key] = { ...levelVessel[key], main: false };
                        } else {
                            value.main = false;
                        }
                    } else if (parallel && value.main) {
                        value.main = false;
                    }
                }

                ids.add(key);
                yes[`${main}`] = { ...yes[`${main}`], [`${levelVessel[key].level}`]: true };
                levelVessel[key] = { ...levelVessel[key], ids: Array.from(new Set(levelVessel[key].ids).add(id)) };
            }
        }
    });

    value.ids = Array.from(ids);
    levelVessel[id] = value;

    if (levelOld) {
        const { [id]: valueOld } = levelOld;
        const duplicate = new Set([...valueOld.ids, ...value.ids]);
        if (duplicate.size === ids.size && valueOld.ids.length > 0 && valueOld.ids.length === value.ids.length)
            return { ...source, [vessel_id]: levelOld };
    }
    // Level adjustment
    if (!value.main) {
        const { level, sub } = value;
        if (yes.false[`${level}`]) {
            const no: any = [];
            for (let i = 1; i <= sub; i++) {
                if (!yes.false[`${i}`])
                    no.push(i);
            }
            if (no[0]) {
                value.level = no[0];
            } else {
                value.sub++;
                value.level = value.sub;
            }
        }

        value.ids.forEach(key => {
            if (levelVessel[key].main)
                return;
            const isHaveMain = levelVessel[key].ids.some(subKey => subKey !== id && levelVessel[subKey].main);
            if (!isHaveMain) {
                levelVessel[key] = { ...levelVessel[key], main: true };
            }
        });
    }

    // Set all nodes to have the same sub
    const check = new Set(value.ids.reduce((rs: string[], key) => [...rs, ...levelVessel[key].ids], value.ids));
    check.forEach(key => levelVessel[key] = { ...levelVessel[key], sub: value.sub });

    return { ...source, [vessel_id]: levelVessel };
}

export function downLevel(source: ILevelByVessel, data: IOperation, ops: IOperations) {
    if (!data) return source;

    const { id: vessel_id } = data.vessel;
    const levelByVessel = source[vessel_id];

    if (!levelByVessel) return source;

    const { id } = data.operation;
    const { [id]: _, ...remainingLevels } = levelByVessel;

    return Object.keys(remainingLevels).reduce<ILevelByVessel>((rs, key) => {
        const item = ops[key];
        if (!item) return rs;

        return upLevel(rs, item, ops);
    }, {
        ...source,
        [vessel_id]: {},
    });
}

export function getContract(data: IOperation): { contract_id: string, available_time_id: string } {
    const { available_time_type, isOwn, permission } = data.vessel;
    const start = data.activity_log[0].est_start_time;
    const finish = data.activity_log[data.activity_log.length - 1].est_start_time;

    if (available_time_type === AVAILABLE_TIME_TYPE.ONLY_AVAILABLE_TIME && !isOwn && !permission) {
        const { contracts = [], available_times = [] } = data.vessel || {};
        const { id: contract_id = "-" } = contracts.find(item => {
            const { start_time, finish_time } = item;
            return item.type === CONTRACT_TYPE.FIXED && start <= finish_time && start_time <= finish;
        }) || {};
        const { id: available_time_id = "-" } = available_times.find(item => {
            const { start_time, finish_time } = item;
            return start <= finish_time && start_time <= finish;
        }) || {};
        return { contract_id, available_time_id };
    }
    return {
        contract_id: "-",
        available_time_id: "-"
    };
}


export function allowUpdateFish(op: IOperation): boolean {
    const opTypes = new Set([OPERATION_TYPE.HARVEST, OPERATION_TYPE.TRANSPORT]);
    return opTypes.has(op.operation.operation_type);
}

export function getKeyUnit(unit: IUnit) {
    const { harvest_id = "-", id } = unit || {};
    return [harvest_id, id].join("/");
}

export function addFishChanges(fishChanges: IFishChanges, newOp: IOperation, oldOp?: TOperation): IFishChanges {
    const { sub_operations, id } = newOp.operation;
    let result = cloneObj(fishChanges);
    if (!allowUpdateFish(newOp) || !newOp.action_type)
        return result;

    const oldSubs = cloneObj((oldOp?.sub_operations || []).reduce((list: any, item) => {
        const key = getKeyUnit(item);
        list[key] = item.fish_amount;
        return list;
    }, {}));

    sub_operations.forEach(item => {
        const key = getKeyUnit(item);
        result[key] = { ...result[key] || {}, [id]: (oldSubs[key] || 0) - item.fish_amount };
    });

    return result;
}

export function removeFishChanges(fishChanges: IFishChanges, op: IOperation): IFishChanges {
    const { sub_operations, id } = op.operation;
    let result = cloneObj(fishChanges);
    if (!allowUpdateFish(op))
        return fishChanges;
    sub_operations.forEach(item => {
        const key = getKeyUnit(item);
        const { [id]: old, ...args } = result[key] || {};
        if (args)
            result = { ...result, [key]: args || {} };
    });
    return result || {};
}

export function getTypesAndUnitOfTreatment(tasks: any[]): {
    units: { [id: string]: boolean },
    types: { [id: string]: string }
} {
    return tasks.reduce((rs: any, item: any) => {
        const { type, treatment_type } = item;
        if (type === TASK_TYPE.COMBINE) {
            const { type_name } = item;
            rs.types[type_name] = type_name;
        } else if (treatment_type === TREATMENT_TYPE.SORTING) {
            const { sub_tasks } = item as ISorting;
            Object.keys(sub_tasks).forEach((key: string) => {
                rs.types[TREATMENT_TYPE.SORTING] = treatmentType[TREATMENT_TYPE.SORTING].name;
                sub_tasks[key].forEach((task) => {
                    if (typeof task.treatment_type === "number")
                        rs.types[task.treatment_type] = treatmentType[task.treatment_type].name;
                });
            });
        } else if (treatment_type !== undefined) {
            const { name = "" } = treatmentType[treatment_type] || {};
            rs.types[treatment_type] = name;
        }
        const { unit_id = "" } = item.sub_operation || {};
        rs.units[unit_id] = true;
        return rs;
    }, { units: {}, types: {} });
}

/*
    Date: 11/12/2024
    Author: Vinh.Pham
    Description: Get fish amount and total weight and fish amount left from unit
 */
export function getFishAmountOfUnit(unit: IUnit, vessel: IVessel) {
    const { capacity } = vessel;
    const { fish_amount, avg_weight } = unit;
    if (avg_weight < 0) {
        return {
            fish_amount: 0,
            total_weight: capacity,
            inventory: 0,
        };
    }

    let amountOrder = fish_amount, inventory;
    const amountDefault = Math.floor(capacity / avg_weight);
    if (amountOrder > amountDefault) {
        amountOrder = amountDefault;
        inventory = fish_amount - amountDefault;
    } else
        inventory = 0;

    return {
        fish_amount: amountOrder,
        total_weight: amountOrder * avg_weight,
        inventory,
    };
}

/*
    Date: 11/12/2024
    Author: Vinh.Pham
    Description: Find the tank containing the delivery method's fish in the operation
 */
export function sortTanks(sub_ops: IUnit[], tanks: IStorage[]): { id: string, total_weight: number }[] {
    const group = sub_ops.reduce((rs: { [id: string]: number }, { delivery_types = [] }) => {
        delivery_types.forEach(({ tanks }) => {
            tanks.forEach(({ id, total_weight = 0 }) => rs[id] = (rs[id] || 0) + total_weight);
        });
        return rs;
    }, tanks.reduce((rs: { [id: string]: number }, item) => {
        rs[item.id || ''] = 0;
        return rs;
    }, {}));

    const sort = Object.keys(group).sort((a, b) => group[a] - group[b]);

    return sort.map(key => ({
        id: key,
        total_weight: group[key]
    }))
}
